import { FlexibleModuleEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '~/modules/page-builder/types';
import SingleImageModule from '../single-image-module/SingleImageModule';

import DuoParagraphModule from '../duo-paragraph-module/DuoParagraphModule';
import DuoImageModule from '../duo-image-module/DuoImageModule';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';
import ImageParagraphModule from '../image-paragraph-module/ImageParagraphModule';
import GalleryModule from '../gallery-module/GalleryModule';
import SingleVideoModule from '../single-video-module/SingleVideoModule';
import VideoParagraphModule from '../video-paragraph-module/VideoParagraphModule';
import QuoteModule from '../quote-module/QuoteModule';
import TrioImageModule from '../trio-image-module/TrioImageModule';
import FullPageGalleryModule from '../../detail/full-page-gallery-module/FullPageGalleryModule';
import FullPageImageModule from '../single-image-module/FullPageImageModule';

interface DetailModuleProps {
    module: PageBuilderModuleType;
    autoPlayVideoModuleId?: string;
    /** Index of this module for eager loading determination */
    moduleIndex?: number;
}

/**
 * A rendered Page Builder module on the website. The module component rendered
 * depends on the module type.
 */
const DetailModuleMobile = ({
    module,
    autoPlayVideoModuleId,
    moduleIndex,
}: DetailModuleProps) => {
    const columns = module.columns;
    const meta = module.meta;

    const firstColumn = columns[0];
    if (!firstColumn) {
        return null;
    }

    switch (module.presetType) {
        case FlexibleModuleEnum.ImageParagraphModule:
            return (
                <ImageParagraphModule
                    columns={columns}
                    meta={meta}
                    moduleIndex={moduleIndex}
                />
            );
        case FlexibleModuleEnum.VideoParagraphModule:
            return <VideoParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.SingleVideoModule:
            return (
                <SingleVideoModule
                    column={firstColumn}
                    shouldAutoPlayVideo={autoPlayVideoModuleId === module.id}
                    isCoverGrayscale={module.isCoverGrayscale}
                />
            );
        case FlexibleModuleEnum.HeadingModule:
        case FlexibleModuleEnum.SingleParagraphModule:
            return <SingleParagraphModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.DuoParagraphModule:
            return <DuoParagraphModule columns={columns} meta={meta} />;
        case FlexibleModuleEnum.QuoteModule:
            return <QuoteModule column={firstColumn} meta={meta} />;
        case FlexibleModuleEnum.SingleImageModule:
            return (
                <SingleImageModule
                    column={firstColumn}
                    moduleIndex={moduleIndex}
                />
            );
        case FlexibleModuleEnum.FullPageImageModule:
            return (
                <FullPageImageModule
                    column={firstColumn}
                    moduleIndex={moduleIndex}
                />
            );
        case FlexibleModuleEnum.DuoImageModule:
            return (
                <DuoImageModule columns={columns} moduleIndex={moduleIndex} />
            );
        case FlexibleModuleEnum.TrioImageModule:
            return (
                <TrioImageModule columns={columns} moduleIndex={moduleIndex} />
            );
        case FlexibleModuleEnum.QuadImageModule:
        case FlexibleModuleEnum.OctaImageModule:
            return (
                <GalleryModule columns={columns} moduleIndex={moduleIndex} />
            );
        case FlexibleModuleEnum.FullPageImagesSliderModule:
            return <FullPageGalleryModule columns={columns} />;
        default:
            return null;
    }
};

export default DetailModuleMobile;
